import { themeColors } from '@/themeColors';
import BackButton from "@/components/Common/BackButton";
import React, { useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  classCardNumber,
} from "@stripe/react-stripe-js";
import TextBox from "@/components/Common/TextBox";
import { savePaymentDetails } from "@/services/paymentsService";
import { getCardElementOptions, toTitleCase } from "@/helpers/common";

const CardInputWrapper = styled.div`
  border: 2px solid #cfd4dd;
  border-radius: 12px;
  padding: 12px;
  height: 47px;
`;

const PaymentComponent = ({ ...props }) => {
  const paymentMethods = useSelector(
    (state) => state.paymentMethods.paymentMethods
  );
  const [cardSave, setCardSave] = useState(false);
  const [name, setName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [nameError, setNameError] = useState("");
  const [zipCodeError, setZipCodeError] = useState("");
  const [errorMessages, setErrorMessages] = useState({
    cardNumberError: "",
    cvvError: "",
    expiryError: "",
  });

  const [cardNumberComplete, setCardNumberCOmplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCVCComplete, setCardCVCComplete] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const re = /^[A-Za-z0-9 -]+$/;
  const handleNameChange = (value) => {
    setNameError("");
    if (value === "" || re.test(value)) {
      setName(value);
    }
    if (value.trim() === "") {
      setNameError("Name on card is incomplete");
    }
  };
  /**
   * To handle zipcode change
   * @param {*} value
   */
  const handleZipCodeChange = (value) => {
    setZipCodeError("");
    if (value === "" || re.test(value)) {
      setZipCode(value);
    }
    if (value.trim() === "") {
      setZipCodeError("ZipCode is incomplete");
    }
  };
  /**
   * To handle card number changes
   * @param {*} e
   */
  const handleCardNumberChange = (e) => {
    setErrorMessages({ ...errorMessages, cardNumberError: "" });
    if (e.complete) {
      setCardNumberCOmplete(true);
    } else {
      setCardNumberCOmplete(false);
    }
    if (e?.error?.message !== "") {
      setErrorMessages({
        ...errorMessages,
        cardNumberError: e?.error?.message,
      });
    }
  };

  const handleCardExpiryChange = (e) => {
    setErrorMessages({ ...errorMessages, expiryError: "" });
    if (e.complete) {
      setCardExpiryComplete(true);
    } else {
      setCardExpiryComplete(false);
    }
    if (e?.error?.message !== "") {
      setErrorMessages({ ...errorMessages, expiryError: e?.error?.message });
    }
  };

  const handleCVCChange = (e) => {
    setErrorMessages({ ...errorMessages, cvvError: "" });
    if (e.complete) {
      setCardCVCComplete(true);
    } else {
      setCardCVCComplete(false);
    }
    if (e?.error?.message !== "") {
      setErrorMessages({ ...errorMessages, cvvError: e?.error?.message });
    }
  };
  /**
   * To handle validation
   * @returns
   */
  const checkValid = () => {
    // Gate only on the inputs we control (name + ZIP). The Stripe element
    // `complete` flags are unreliable when fields are populated via browser
    // autofill (the change events don't always fire), which would otherwise
    // leave the button permanently disabled. Card completeness is validated
    // authoritatively by stripe.createToken() in handleSubmit.
    if (name.trim() !== "" && zipCode.trim() !== "" && !submitClicked) {
      return false;
    } else {
      return true;
    }
  };
  /**
   * To handle add payment method
   * @param {*} e
   * @returns
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitClicked(true);
    if (!stripe || !elements) {
      setSubmitClicked(false);
      return;
    }

    const cardNumberElement = elements.getElement("cardNumber");
    const { token, error } = await stripe.createToken(cardNumberElement, {
      name: name,
      address_zip: zipCode,
    });
    if (error) {
      setCardSave(false);
      setSubmitClicked(false);
      toast.error(error.message);
    } else {
      if (token?.id) {
        const data = {
          Name: name,
          ZipCode: zipCode,
          TokenId: token?.id,
          CardId: token?.card?.id,
          Brand: token?.card?.brand,
          CVC_Check: token?.card?.cvc_check,
          Exp_month: token?.card?.exp_month,
          Exp_year: token?.card?.exp_year,
          Funding: token?.card?.funding,
          Last4: token.card.last4,
          IsDefault: paymentMethods?.length > 0 ? 0 : 1,
        };

        await savePaymentDetails(data)
          .then((response) => {
            props.onHide();
            if (response.errorCode === 0) {
              toast.success("Payment method successfully added!");
              props.updateReloadParam();
              setSubmitClicked(false);
            } else {
              toast.error(response.message);
              setSubmitClicked(false);
            }
          })
          .catch((err) => {
            setSubmitClicked(false);
            toast.error(err);
          });
      } else {
        setSubmitClicked(false);
      }
    }
  };

  const handleCancel = () => {
    props.onHide();
  };

  return (
    <form>
      {props.fromSubscriptionComponent && (
        <BackButton onClick={() => props.onHide()} />
      )}
      <h3 className="text-start">Add a payment method</h3>
      {props.fromSubscriptionComponent && (
        <p className="desc">
          We’ll use this card to bill your subscription plan.
        </p>
      )}
      <Row>
        <Col xl={12}>
          <div className="textbox-wrap">
            <label className="form-label">Name on Card</label>
            <TextBox
              id="cardname"
              name="cardname"
              type="text"
              maxLength={50}
              value={toTitleCase(name)}
              placeholder="John Doe"
              onKeyDown={(e) => {
                const invalidChars = /[^a-zA-Z\s]/; // Regex to allow only alphabets and spaces
                if (invalidChars.test(e.key) && e.key !== "Backspace") {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const filteredValue = e.target.value.replace(
                  /[^a-zA-Z\s]/g,
                  ""
                ); // Remove non-alphabetic characters
                handleNameChange(filteredValue);
              }}
              className="form-control"
              errorClass="error-validation-msg h-10"
              errorMessage={nameError}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="textbox-wrap">
            <label className="form-label">Card Number</label>
            <CardInputWrapper className="stripe-field mb-0">
              <CardNumberElement
                onChange={(e) => handleCardNumberChange(e)}
                placeholder="Custom card number placeholder"
                className={"stripe-control " + classCardNumber}
                options={getCardElementOptions("**** **** **** ****")}
              />
            </CardInputWrapper>
            {<div className="error-msg">{errorMessages?.cardNumberError}</div>}
          </div>
        </Col>
      </Row>
      <Row>
        <Col className="col-md" xs={12}>
          <div className="textbox-wrap">
            <label className="form-label">Date of Expiration</label>
            <CardInputWrapper className="stripe-field mb-0">
              <CardExpiryElement
                onChange={(e) => handleCardExpiryChange(e)}
                className={"stripe-control " + classCardNumber}
                options={getCardElementOptions("")}
              />
            </CardInputWrapper>
            {<span className="error-msg">{errorMessages?.expiryError}</span>}
          </div>
        </Col>
        <Col className="col-md" xs={12}>
          <div className="textbox-wrap">
            <label className="form-label">CVC</label>
            <CardInputWrapper className="stripe-field mb-0">
              <CardCvcElement
                onChange={(e) => handleCVCChange(e)}
                className={"stripe-control " + classCardNumber}
                options={getCardElementOptions("")}
              />
            </CardInputWrapper>
            {<span className="error-msg">{errorMessages?.cvvError}</span>}
          </div>
        </Col>
        <Col className="col-md" xs={12}>
          <div className="textbox-wrap">
            <label className="form-label">ZIP Code</label>
            <TextBox
              id="zipcode"
              name="zipcode"
              type="text"
              maxLength={10}
              value={zipCode}
              onKeyDown={(e) => {
                const invalidChars = /[^0-9]/; // Regex to allow only numbers
                if (invalidChars.test(e.key) && e.key !== "Backspace") {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const filteredValue = e.target.value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
                handleZipCodeChange(filteredValue);
              }}
              className="form-control"
              errorClass="error-validation-msg h-10"
              errorMessage={zipCodeError}
            />
          </div>
        </Col>
      </Row>
      <div className="modal-btn-wrap">
        <Button className="btn btn-tertiary me-3" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={checkValid()}
          onClick={handleSubmit}
        >
          Done
        </Button>
      </div>
    </form>
  );
};

export default PaymentComponent;
